import { useNavigate } from "react-router-dom";
import { FiUsers, FiUserX, FiShield, FiCalendar } from "react-icons/fi";
import { getEmployees } from "../../services/employeeApi";
import { getAuditTrail, getRatingSettings } from "../../services/settingsApi";
import { listAccounts } from "../../services/accountApi";
import { getMe } from "../../services/core";
import { useCycle } from "../../context/CycleContext";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, StatTile, Loader, ErrorState, DataTable, Button } from "../../components/common";
import { ROLE_LABELS } from "../../services/navigation";
import { formatDateTime, formatDate } from "../../utils/format";

const api = { getEmployees, getAuditTrail, getRatingSettings, listAccounts, getMe };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { activeCycle } = useCycle();
  const { data, loading, error, reload } = useAsync(async () => {
    // [UPDATED] "Recent activity" shows only what the signed-in admin did (audit-logs filtered by actor_employee_id).
    const me = await api.getMe();
    const [emps, audit, rating, accounts] = await Promise.all([
      api.getEmployees(), api.getAuditTrail({ limit: 30, actorId: me.employee_id }), api.getRatingSettings(), api.listAccounts(),
    ]);
    return { employees: emps.data, audit: audit.data, rating: rating.data, accounts: accounts.data };
  }, []);

  if (loading || !data) return error ? <ErrorState message={error} onRetry={reload} /> : <Loader />;

  const active = data.employees.filter((e) => e.status === "Active");
  const byRole = Object.keys(ROLE_LABELS).map((role) => ({ role, count: active.filter((e) => e.role === role || e.roles.includes(role)).length }));
  const activeAccounts = data.accounts.filter((a) => a.active);
  const w = data.rating.weights;

  return (
    <div className="page">
      <PageHeader title="Administration" subtitle="Accounts, access and configuration for the PMS module." actions={<Button icon={FiShield} onClick={() => navigate("/admin/users")}>Manage users & roles</Button>} />

      <div className="stat-grid">
        <StatTile icon={FiUsers} label="Active accounts" value={activeAccounts.length} hint={`${active.length} active employees`} onClick={() => navigate("/admin/users")} />
        <StatTile icon={FiUserX} tone="warning" label="Blocked from login" value={data.accounts.length - activeAccounts.length} hint={`${active.length - data.accounts.filter((a) => active.some((e) => e.id === a.employeeId)).length} active employees without a login`} />
        <StatTile icon={FiCalendar} tone="info" label="Active cycle" value={activeCycle?.name || "None"} hint={activeCycle ? `Ends ${formatDate(activeCycle.endDate)}` : "Create one under cycles"} onClick={() => navigate("/hr/cycles")} />
        <StatTile icon={FiShield} tone="success" label="Score weighting" value={`${w.goals} / ${w.kra}`} hint="Goals / KRA" onClick={() => navigate("/hr/rating-settings")} />
      </div>

      <div className="grid-1-2">
        <Card title="Accounts by role">
          <ul className="role-count-list">
            {byRole.map((r) => (
              <li key={r.role}>
                <span>{ROLE_LABELS[r.role]}</span>
                <strong>{r.count}</strong>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="My recent activity" subtitle="Changes you made as admin" padded={false}>
          <DataTable keepOrder
            dense
            pageSize={6}
            rows={data.audit}
            emptyTitle="No activity yet"
            emptyText="Changes you make (users, roles, settings) appear here."
            columns={[
              { key: "at", label: "When", render: (r) => formatDateTime(r.at) },
              { key: "actorName", label: "By" },
              { key: "action", label: "Action", render: (r) => `${r.entityType}: ${r.action}` },
              { key: "employeeName", label: "Employee" },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}